/**
 * Synced from convex/lib/productFeedbackConstants.ts (universal feedback wire contract).
 */

export const PRODUCT_FEEDBACK_SURFACES = ['cli', 'mcp', 'cortex', 'api', 'website'] as const;
export const PRODUCT_FEEDBACK_ACTORS = ['human', 'agent', 'human-via-agent'] as const;
export const PRODUCT_FEEDBACK_CATEGORIES = ['bug', 'friction', 'idea', 'praise', 'other'] as const;
// WP-494 §4: 'screening' is the pre-queue quarantine status every insert stamps first —
// added additively (schema union derives from this array).
export const PRODUCT_FEEDBACK_STATUSES = ['new', 'triaged', 'planned', 'resolved', 'dismissed', 'screening'] as const;
// WP-494 §4 review fix (Codex P2): 'screening' is a SYSTEM-only status — it is stamped by
// `insertFeedback` and cleared by `releaseFromScreening`; a vendor pushing a row BACK into
// 'screening' via the status verb would strand it (no `screenFeedback` gets scheduled) until
// another manual status change. Derived from the SSOT array (one place to keep them in sync)
// so a future status addition is vendor-settable by default unless excluded here too.
// Copilot review fix: the predicate is a TYPE GUARD, not a bare boolean. A plain
// `.filter((s) => s !== 'screening')` does not narrow the element type, so
// `VendorSettableStatus` below still included 'screening' at the TYPE level while runtime
// validation rejected it — exactly the divergence the derive-from-SSOT comment above exists
// to prevent. The guard + the explicit `Exclude<...>` alias keep types and runtime aligned.
export const VENDOR_SETTABLE_STATUSES = PRODUCT_FEEDBACK_STATUSES.filter(
	(status): status is VendorSettableStatus => status !== 'screening',
);

// Review fix (P1): `context.client` is a free-text label the CALLER supplies (MCP tool
// arg, CLI flag) describing which specific tool submitted — NOT `PRODUCT_FEEDBACK_SURFACES`
// (the system-derived, gateway-stamped `row.surface`). A structural allowlist of the
// literal values every known caller sends today: packages/cli/src/commands/feedback.ts
// ('pb-cli'), packages/mcp-server/src/tools/feedback.ts ('mcp' default), and
// FeedbackDialog.svelte ('cortex'). `redactContext` (convex/productFeedback.ts) collapses
// anything outside this set to 'unknown' — same fail-closed, hand-synced-allowlist posture
// as `KNOWN_WORKSPACE_ROUTE_SEGMENTS`/`KNOWN_TOP_ROUTE_SEGMENTS`. Add a literal here ONLY
// when a real caller starts sending it.
export const PRODUCT_FEEDBACK_KNOWN_CLIENTS = ['mcp', 'pb-cli', 'cortex'] as const;

export type ProductFeedbackSurface = (typeof PRODUCT_FEEDBACK_SURFACES)[number];
export type ProductFeedbackActor = (typeof PRODUCT_FEEDBACK_ACTORS)[number];
export type ProductFeedbackCategory = (typeof PRODUCT_FEEDBACK_CATEGORIES)[number];
export type ProductFeedbackStatus = (typeof PRODUCT_FEEDBACK_STATUSES)[number];
export type VendorSettableStatus = Exclude<ProductFeedbackStatus, 'screening'>;
export type ProductFeedbackKnownClient = (typeof PRODUCT_FEEDBACK_KNOWN_CLIENTS)[number];
